import React, { FunctionComponent, ReactNode } from 'react';
import {
  MenuGroup,
  MenuGroupHeader,
  MenuList,
  MenuDivider,
  mergeClasses,
} from '@fluentui/react-components';
import { SearchBox } from './components/search-box';
import { useScopeSelectorStyles } from './styles';
import {
  getScopeFiltersChildComponent,
  geti18nStringTemplates,
} from './helper';
import { ScopeBarScrollingContainer, ScopePanelScrollingContainer } from './components/scrolling-container';
import { withEntitySearch } from './with-entity-search';
import { Breadcrumbs } from './components/scope-breadcrumbs';
import { ScopeMenuItemListLoader, ScopeMenuItemListHeader } from './components';
import { Entity } from './types';

export interface IScopeSelectorProps {
  i18n: any;
  entityList: Entity[];
  entityTypeDisplayName: string;
  onSearchChange: (event) => void;
  className?: string;
  inMenuPopover?: boolean;
  filters?: ReactNode;
  rootEntityTypeDisplayName: string;
  selectedEntity?: Entity;
  hideSubEntitySelector?: boolean;
  hasRootEntityFilter?: boolean;
  hasSubEntityFilter?: boolean;
  addScope: (entity: Entity) => void;
  removeScope: (entity?: Entity) => void;
  setPopoverOpen?: (open: boolean) => void;
  children: ReactNode;
  style?: React.CSSProperties;
}

const ScopeSelector: FunctionComponent<IScopeSelectorProps> = (props) => {
  const {
    i18n,
    selectedEntity,
    entityList,
    inMenuPopover,
    addScope,
    removeScope,
    children,
    className,
    rootEntityTypeDisplayName,
    entityTypeDisplayName,
    onSearchChange,
    setPopoverOpen,
    style,
    hideSubEntitySelector,
    hasSubEntityFilter,
    hasRootEntityFilter,
    ...rest
  } = props;

  const styles = useScopeSelectorStyles();
  const filters = getScopeFiltersChildComponent(children);
  const ScrollingContainer = inMenuPopover ? ScopeBarScrollingContainer : ScopePanelScrollingContainer;
  const stringTemplates = geti18nStringTemplates(i18n);

  const searchBoxProps = {
    placeholder: stringTemplates.searchEntity(entityTypeDisplayName),
    onSearchChange,
  };

  const breadcrumbProps = {
    rootEntityTypeDisplayName,
    selectedEntity,
    hasRootEntityFilter,
    i18n,
  };

  return (
    <MenuList className={mergeClasses(styles.root, className)} style={style}>
      <MenuGroup>
        <MenuGroupHeader>
          <Breadcrumbs {...breadcrumbProps} />
        </MenuGroupHeader>
        {!hideSubEntitySelector && <SearchBox {...searchBoxProps} />}
        {filters && React.cloneElement(filters, { ...rest })}
      </MenuGroup>
      {!hideSubEntitySelector && (
      <>
        <MenuDivider />
        <MenuGroup className={styles.listMenuGroup}>
          <ScopeMenuItemListHeader
            i18n={i18n}
            rootEntityTypeDisplayName={rootEntityTypeDisplayName}
            selectedEntity={selectedEntity}
            addScope={addScope}
            removeScope={removeScope}
            setPopoverOpen={setPopoverOpen}
            hasRootEntityFilter={hasRootEntityFilter}
            hasSubEntityFilter={hasSubEntityFilter}
          />
          <ScrollingContainer>
            <ScopeMenuItemListLoader
              i18n={i18n}
              entityList={entityList}
              addScope={addScope}
              entityTypeDisplayName={entityTypeDisplayName}
            />
          </ScrollingContainer>
        </MenuGroup>
      </>
      )}
    </MenuList>
  );
};

ScopeSelector.defaultProps = {
  className: '',
  inMenuPopover: false,
  selectedEntity: undefined,
  filters: undefined,
  hasRootEntityFilter: false,
  setPopoverOpen: undefined,
  style: undefined,
  hideSubEntitySelector: false,
};

const ScopeSelectorWithSearch = withEntitySearch<IScopeSelectorProps>(ScopeSelector);

export { ScopeSelectorWithSearch as ScopeSelector };
